'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-connect/attachments.mdx
 * (published at user-docs.totalaccess.co.za/total-connect/attachments)
 */

/**
 * Email Attachment Viewer
 *
 * Displays email attachments with:
 * - Thumbnails for images
 * - Icon-based previews for documents
 * - Full-screen preview modal
 * - Explicit download button (not auto-download)
 *
 * Attachments are stored in thread-storage and accessed via URLs
 */

import UserDocsLink from '@/components/shared/UserDocsLink';
import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Download, ExternalLink, Maximize2, FileText, Image as ImageIcon,
  FileVideo, FileAudio, File as FileIcon, FileArchive, FileSpreadsheet, FileCode,
  Loader2, ZoomIn, ZoomOut, RotateCw, FileSignature, PenTool, Calendar, Lock, Presentation,
  ChevronDown, Paperclip
} from 'lucide-react';
import { cn } from '@/lib/utils';
import dynamic from 'next/dynamic';
import Papa from 'papaparse';
import { isCapacitor } from '@/lib/platformUtils';
import { getEndpointUrl } from '@/lib/apiConfig';
import { authService } from '@/lib/authService';
import offlineDb from '@/lib/offline/offlineDb';
import { notify } from '@/lib/notifications';

// Offline database logging helper
const DEBUG_LOG_KEY = 'attachment_debug_log';
const MAX_DEBUG_LOG_ENTRIES = 500; // Prevent DB bloat

async function logToFile(message: string): Promise<void> {
  if (typeof window === 'undefined') return;

  // Always log to console
  console.log(message);

  // Store in offline database for persistence across sessions
  if (offlineDb) {
    try {
      const timestamp = new Date().toISOString();
      const logEntry = `[${timestamp}] ${message}`;

      // Get existing logs
      const existingMeta = await offlineDb.meta.get(DEBUG_LOG_KEY);
      let logs: string[] = [];

      if (existingMeta) {
        try {
          logs = JSON.parse(existingMeta.payload);
        } catch {
          logs = [];
        }
      }

      // Add new log entry
      logs.push(logEntry);

      // Implement rotation - keep only last N entries
      if (logs.length > MAX_DEBUG_LOG_ENTRIES) {
        logs = logs.slice(-MAX_DEBUG_LOG_ENTRIES);
      }

      // Store back in meta table
      await offlineDb.meta.put({
        key: DEBUG_LOG_KEY,
        domain: 'app-shell',
        client_id: 0,
        updated_at: new Date().toISOString(),
        payload: JSON.stringify(logs),
      });
    } catch (e) {
      // Silently fail if offline DB write fails
    }
  }
}

// Lazy-load PDF editor (heavy component with pdf-lib + signature canvas)
const PDFEditorOverlay = dynamic(
  () => import('./PDFEditorOverlay'),
  { ssr: false }
);

const OfficeAttachmentPreview = dynamic(
  () => import('./OfficeAttachmentPreview'),
  { ssr: false }
);

// Cloud storage URL from environment
const CLOUD_STORAGE_URL = process.env.NEXT_PUBLIC_CLOUD_STORAGE_URL || 'https://cloud-storage.totalaccess.co.za';

interface EmailAttachment {
  filename: string;
  contentType: string;
  size: number;
  url?: string;
  directUrl?: string;
  storage_token?: string;
  id?: string;
  partId?: string;
  contentId?: string;
  inline?: boolean; // Inline/embedded attachments (CID references)
  upload_error?: boolean;
}

interface EmailAttachmentViewerProps {
  attachments: EmailAttachment[];
  messageId: string;
  className?: string;
  /** The rendered HTML body of the email — used to detect embedded images whose URLs appear inline */
  htmlBody?: string;
  /** Called when user wants to reply with an edited/signed PDF attached */
  onReplyWithAttachment?: (file: File) => void;
  /** Called after saving edited PDF to cloud storage */
  onSavedToCloud?: (fileInfo: { id: string; name: string; url: string }) => void;
  /** Called when user removes an attachment (composer mode) - receives the attachment index */
  onRemoveAttachment?: (index: number) => void;
  /** Force compact thumbnail sizing (used in compose/forward views) */
  forceCompact?: boolean;
  /** Whether thumbnails should start collapsed (e.g. in composer on mobile) */
  defaultCollapsed?: boolean;
}

interface AttachmentPreviewModalProps {
  attachment: EmailAttachment;
  messageId: string;
  isOpen: boolean;
  onClose: () => void;
  onEditPdf?: (pdfUrl: string, filename: string) => void;
}

// Get file icon based on content type
function getFileExtension(filename?: string): string {
  const normalized = (filename || '').trim().toLowerCase();
  const dotIndex = normalized.lastIndexOf('.');
  return dotIndex >= 0 ? normalized.slice(dotIndex + 1) : '';
}

function getFileIcon(contentType: string, filename?: string) {
  const extension = getFileExtension(filename);
  if (contentType.startsWith('image/')) return ImageIcon;
  if (contentType.startsWith('video/')) return FileVideo;
  if (contentType.startsWith('audio/')) return FileAudio;
  if (contentType.includes('pdf')) return FileText;
  if (contentType.includes('calendar') || contentType.includes('ics')) return Calendar;
  if (contentType.includes('presentation') || contentType.includes('powerpoint') || ['ppt', 'pptx', 'odp'].includes(extension)) return Presentation;
  if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType.includes('csv')) return FileSpreadsheet;
  if (['xls', 'xlsx', 'csv', 'ods'].includes(extension)) return FileSpreadsheet;
  if (contentType.includes('zip') || contentType.includes('rar') || contentType.includes('tar') || contentType.includes('gzip')) return FileArchive;
  if (contentType.includes('javascript') || contentType.includes('json') || contentType.includes('xml') || contentType.includes('html')) return FileCode;
  if (['doc', 'docx', 'odt', 'rtf', 'txt'].includes(extension)) return FileText;
  if (contentType.includes('text') || contentType.includes('document') || contentType.includes('word')) return FileText;
  return FileIcon;
}

function isPreviewableOfficeAttachmentContent(contentType: string, filename?: string): boolean {
  const extension = getFileExtension(filename);
  const normalizedType = (contentType || '').toLowerCase();

  return (
    ['docx', 'xlsx', 'xls', 'pptx'].includes(extension) ||
    normalizedType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    normalizedType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    normalizedType === 'application/vnd.ms-excel' ||
    normalizedType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
    normalizedType === 'application/vnd.ms-powerpoint'
  );
}

// Check if file can be previewed inline
function canPreview(contentType: string, filename?: string): boolean {
  const ct = (contentType || '').toLowerCase();
  const fn = (filename || '').toLowerCase();
  if (isCsvContent(ct, fn)) return true;
  if (isPreviewableOfficeAttachmentContent(ct, fn)) return true;
  return (
    ct.startsWith('image/') ||
    ct === 'application/pdf' ||
    ct.startsWith('video/') ||
    ct.startsWith('audio/') ||
    ct.startsWith('text/') ||
    ct.includes('calendar') ||
    (ct === 'application/octet-stream' && false)
  );
}

/** CSV preview — tolerate mis-labelled octet-stream when filename ends with .csv */
function isCsvContent(contentType: string, filename: string): boolean {
  const ct = contentType.toLowerCase();
  const fn = filename.toLowerCase();
  if (fn.endsWith('.csv')) return true;
  return ct === 'text/csv' || ct === 'application/csv' || ct.endsWith('+csv');
}

// Known image file extensions (user-attached images always have these)
const IMAGE_EXTENSIONS = new Set([
  'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tiff', 'tif', 'heic', 'heif', 'avif',
]);

/**
 * Detect whether an image attachment is likely an embedded/inline image rather
 * than a user-intentionally-attached file.  Embedded images come from:
 *  - CID-referenced images inside multipart/related MIME (contentId / inline flag)
 *  - Gmail-forwarded emails where inline images become regular attachments
 *    without contentId — these typically have filenames WITHOUT a recognised
 *    image file extension (e.g. "bannerMobile", "miniInterfileLogo", "image001")
 *  - Broken uploads (upload_error with no usable URL)
 *
 * When an optional `htmlBody` is provided we also check whether the attachment's
 * cloud-storage URL already appears inside the HTML — if so the image is
 * rendered inline and shouldn't be listed again as an attachment.
 */
function isLikelyEmbeddedImage(att: EmailAttachment, htmlBody?: string): boolean {
  // Only image/* attachments can be embedded images.  A PDF or document
  // with a contentId (Gmail assigns contentIds to all MIME parts) is a
  // real attachment, not an embedded image.
  if (!att.contentType?.startsWith('image/')) return false;

  // Explicit inline flag from the backend
  if (att.inline) return true;

  // contentId only counts as embedded if the image is actually referenced
  // in the HTML body via cid: — otherwise it's a regular image attachment
  // that happens to have a contentId (Gmail does this for all attachments).
  if (att.contentId && htmlBody && typeof htmlBody === 'string') {
    const cid = att.contentId.replace(/[<>]/g, '');
    if (htmlBody.includes(`cid:${cid}`) || htmlBody.includes(att.contentId)) {
      return true;
    }
  }

  // Broken upload with no usable URL — nothing to show the user
  if (att.upload_error && !att.url && !att.directUrl && !att.storage_token) return true;

  // If we have the HTML body, check whether the attachment URL is referenced in it
  if (htmlBody && typeof htmlBody === 'string') {
    const url = att.directUrl || att.url;
    if (url && htmlBody.includes(url)) return true;
    // Also check just the storage token path segment
    if (att.storage_token && htmlBody.includes(att.storage_token)) return true;
  }

  // Heuristic: image filenames from MIME inline parts typically lack a file extension.
  // User-attached images from a filesystem always have one (.jpg, .png, etc.).
  const filename = att.filename || '';
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex <= 0) return true; // no extension at all (e.g. "bannerMobile")
  const ext = filename.substring(dotIndex + 1).toLowerCase();
  if (!IMAGE_EXTENSIONS.has(ext)) return true; // has a "." but not a real image extension

  return false;
}

// Also check by filename extension (e.g. contentType may be application/octet-stream)
function isIcsFile(attachment: EmailAttachment): boolean {
  return (
    attachment.contentType?.includes('calendar') ||
    attachment.filename?.toLowerCase().endsWith('.ics') ||
    attachment.filename?.toLowerCase().endsWith('.ifb')
  );
}

function isCsvFile(attachment: EmailAttachment): boolean {
  return isCsvContent(
    attachment.contentType?.toLowerCase() || '',
    attachment.filename?.toLowerCase() || '',
  );
}

function isOfficePreviewAttachment(attachment: EmailAttachment): boolean {
  return isPreviewableOfficeAttachmentContent(
    attachment.contentType?.toLowerCase() || '',
    attachment.filename?.toLowerCase() || '',
  );
}

// Rewrite legacy thread-storage URLs to cloud-storage
// thread-storage is deprecated and no longer exists
function rewriteLegacyUrl(url: string): string {
  if (!url) return url;
  // Rewrite threadstorage.totalaccess.co.za to cloud-storage.totalaccess.co.za
  if (url.includes('threadstorage.totalaccess.co.za')) {
    return url.replace('https://threadstorage.totalaccess.co.za', CLOUD_STORAGE_URL)
      .replace('http://threadstorage.totalaccess.co.za', CLOUD_STORAGE_URL);
  }
  return url;
}

/**
 * Resolve a relative URL to an absolute URL on Capacitor/mobile.
 * On web, relative URLs work fine. On Capacitor, the WebView may not
 * resolve relative URLs correctly for <img>/<iframe> src attributes
 * because CapacitorHttp intercepts fetch() but not resource loads.
 */
function resolveUrlForPlatform(url: string): string {
  if (!url) return url;
  // Already absolute
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // On Capacitor, convert relative URLs to absolute
  if (typeof window !== 'undefined' && isCapacitor()) {
    return getEndpointUrl(url);
  }
  return url;
}

// Get attachment URL from various sources
function getAttachmentUrl(attachment: EmailAttachment, messageId?: string): string | null {
  // E2E-encrypted attachments: the backend rewrites directUrl/url to a
  // relative decrypt endpoint path. Route these through the Next.js email
  // API proxy so they get auth headers and are forwarded to the Go decrypt
  // endpoint.  Handle both /attachments/decrypt and /emails/attachments/decrypt
  // formats (the backend path was shortened in a recent fix).
  const decryptMatch = /\/(emails\/)?attachments\/decrypt[?]/;
  const dUrl = attachment.directUrl;
  const aUrl = attachment.url;
  if (dUrl && decryptMatch.test(dUrl)) {
    // Normalize: strip any leading /emails/ prefix, then prepend /api/v1/email
    const normalized = dUrl.replace(/^\/emails\//, '/');
    return resolveUrlForPlatform(`/api/v1/email${normalized}`);
  }
  if (aUrl && decryptMatch.test(aUrl)) {
    const normalized = aUrl.replace(/^\/emails\//, '/');
    return resolveUrlForPlatform(`/api/v1/email${normalized}`);
  }

  // Cloud storage URLs (new format) - prefer directUrl as it's the full URL
  if (attachment.directUrl) return rewriteLegacyUrl(attachment.directUrl);
  if (attachment.url) {
    const url = rewriteLegacyUrl(attachment.url);
    // If url is a /files/ path, prepend cloud storage domain (no auth required)
    if (url.startsWith('/files/')) {
      return `${CLOUD_STORAGE_URL}${url}`;
    }
    // If it's already a full URL, use as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // For relative /api/ paths, resolve to absolute on Capacitor
    return resolveUrlForPlatform(url);
  }
  // Legacy thread-storage format - use public attachments endpoint (no auth required)
  if (attachment.storage_token) {
    return `${CLOUD_STORAGE_URL}/api/v1/attachments/${attachment.storage_token}`;
  }
  // Fallback: fetch from IMAP via email API proxy (includes auth from browser session)
  // This handles received emails where attachments aren't stored in cloud storage
  if (messageId && (attachment.partId || attachment.filename)) {
    const partId = attachment.partId || attachment.filename;
    return resolveUrlForPlatform(`/api/v1/email/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(partId)}`);
  }
  return null;
}

// Get thumbnail URL for images
function getThumbnailUrl(attachment: EmailAttachment, messageId?: string): string | null {
  if (!attachment.contentType.startsWith('image/')) return null;

  // E2E-encrypted attachments: route through the decrypt endpoint
  const decryptMatch = /\/(emails\/)?attachments\/decrypt[?]/;
  if (attachment.directUrl && decryptMatch.test(attachment.directUrl)) {
    const normalized = attachment.directUrl.replace(/^\/emails\//, '/');
    return resolveUrlForPlatform(`/api/v1/email${normalized}`);
  }
  if (attachment.url && decryptMatch.test(attachment.url)) {
    const normalized = attachment.url.replace(/^\/emails\//, '/');
    return resolveUrlForPlatform(`/api/v1/email${normalized}`);
  }

  // Use directUrl if available (full URL, no auth needed for /files/)
  if (attachment.directUrl) {
    const url = rewriteLegacyUrl(attachment.directUrl);
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}thumb=true`;
  }

  // Use url field (most common case)
  if (attachment.url) {
    let url = rewriteLegacyUrl(attachment.url);
    // If /files/ path, prepend cloud storage domain
    if (url.startsWith('/files/')) {
      url = `${CLOUD_STORAGE_URL}${url}`;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}thumb=true`;
  }

  // Fallback to storage_token (public endpoint, no auth needed)
  if (attachment.storage_token) {
    return `${CLOUD_STORAGE_URL}/api/v1/attachments/${attachment.storage_token}?thumb=true`;
  }

  // Fallback: fetch from IMAP (no thumbnail support, return full URL via local proxy)
  if (messageId && (attachment.partId || attachment.filename)) {
    const partId = attachment.partId || attachment.filename;
    return resolveUrlForPlatform(`/api/v1/email/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(partId)}`);
  }

  return null;
}

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

// ── ICS calendar event parser & card ─────────────────────────────────────────

interface IcsEvent {
  summary: string;
  dtStart: string | null;
  dtEnd: string | null;
  description: string;
  location: string;
  organizer: string;
  attendees: string[];
  status: string;
  uid: string;
}

/** Unfold RFC 5545 lines (CRLF + whitespace = continuation) then parse fields */
function parseIcs(raw: string): IcsEvent {
  const unfolded = raw.replace(/\r\n[ \t]/g, '').replace(/\n[ \t]/g, '');
  const lines = unfolded.split(/\r?\n/);

  const get = (key: string): string => {
    const re = new RegExp(`^${key}(?:;[^:]*)?:(.*)`, 'i');
    for (const line of lines) {
      const m = line.match(re);
      if (m) return m[1].replace(/\\n/g, '\n').replace(/\\,/g, ',').replace(/\\;/g, ';').trim();
    }
    return '';
  };

  const getAll = (key: string): string[] => {
    const re = new RegExp(`^${key}(?:;[^:]*)?:(.*)`, 'i');
    return lines
      .filter(l => re.test(l))
      .map(l => {
        const m = l.match(re)!;
        // Extract CN= if present, else just the email (mailto:)
        const full = m[1];
        const cnMatch = full.match(/CN=([^;:]+)/i);
        const mailMatch = full.match(/mailto:(.+)/i);
        return cnMatch ? cnMatch[1].trim() : mailMatch ? mailMatch[1].trim() : full.trim();
      });
  };

  const formatDt = (val: string): string | null => {
    if (!val) return null;
    // Handle VALUE=DATE (all-day: YYYYMMDD) vs DATETIME (YYYYMMDDTHHmmssZ)
    const m = val.match(/(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2}))?/);
    if (!m) return val;
    const [, y, mo, d, h, min] = m;
    if (!h) return `${y}-${mo}-${d}`;
    return `${y}-${mo}-${d} ${h}:${min}`;
  };

  // Organizer: prefer CN= display name
  const orgRaw = lines.find(l => /^ORGANIZER/i.test(l)) || '';
  const orgCn = orgRaw.match(/CN=([^;:]+)/i)?.[1]?.trim() || '';
  const orgMail = orgRaw.match(/mailto:(.+)/i)?.[1]?.trim() || '';
  const organizer = orgCn ? (orgMail ? `${orgCn} <${orgMail}>` : orgCn) : orgMail;

  return {
    summary: get('SUMMARY') || 'Calendar Event',
    dtStart: formatDt(get('DTSTART')),
    dtEnd: formatDt(get('DTEND')),
    description: get('DESCRIPTION'),
    location: get('LOCATION'),
    organizer,
    attendees: getAll('ATTENDEE'),
    status: get('STATUS'),
    uid: get('UID'),
  };
}

/** Inline CSV preview — fetch text (with JWT when needed) and render a capped table */
function CsvPreviewPanel({
  url,
  onLoaded,
  onError,
}: {
  url: string;
  onLoaded: () => void;
  onError: (message: string) => void;
}) {
  const [rows, setRows] = useState<string[][]>([]);
  const [truncated, setTruncated] = useState(false);
  const [warn, setWarn] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setReady(false);
      try {
        const headers: Record<string, string> = {};
        if (url.includes('/api/v1/')) {
          const tokens = authService.getTokens();
          if (tokens?.access_token) headers.Authorization = `Bearer ${tokens.access_token}`;
        }
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        let text = await res.text();
        if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

        const parsed = Papa.parse<string[]>(text, {
          skipEmptyLines: 'greedy',
        });

        if (cancelled) return;

        const errs = parsed.errors?.filter((e) => e.type !== 'Quotes') ?? [];
        if (errs.length > 0) {
          setWarn(errs.slice(0, 3).map((e) => e.message || String(e.row)).join(' · '));
        } else {
          setWarn(null);
        }

        const data = (parsed.data as string[][]).filter((row) =>
          row.some((cell) => String(cell ?? '').trim() !== ''),
        );
        const maxRows = 502;
        setTruncated(data.length > maxRows);
        setRows(data.slice(0, maxRows));
      } catch (e) {
        if (!cancelled) {
          onError(e instanceof Error ? e.message : 'Failed to load CSV');
        }
      } finally {
        if (!cancelled) {
          setReady(true);
          onLoaded();
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [url, onLoaded, onError]);

  if (!ready) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center text-gray-300 p-8">
        <FileSpreadsheet className="w-14 h-14 mx-auto mb-3 text-gray-500" />
        <p className="text-sm">This CSV has no rows to preview.</p>
      </div>
    );
  }

  const colCount = Math.max(1, ...rows.map((r) => r.length));

  return (
    <div className="w-full max-w-[min(96vw,1200px)] max-h-[calc(100vh-120px)] flex flex-col gap-2">
      {warn && (
        <p className="text-xs text-amber-300 px-1">{warn}</p>
      )}
      {truncated && (
        <p className="text-xs text-gray-400 px-1">
          Showing first {rows.length} rows — download the file for the full dataset.
        </p>
      )}
      <div className="flex-1 overflow-auto rounded-lg border border-gray-700 bg-gray-950">
        <table className="min-w-full text-left text-xs text-gray-100 border-collapse">
          <tbody>
            {rows.map((row, ri) => (
              <tr
                key={ri}
                className={ri === 0 ? 'bg-gray-800/90 font-semibold sticky top-0 z-[1]' : 'even:bg-gray-900/40'}
              >
                {Array.from({ length: colCount }, (_, ci) => (
                  <td
                    key={ci}
                    className="border border-gray-700 px-2 py-1 align-top max-w-[280px] whitespace-nowrap overflow-hidden text-ellipsis"
                    title={String(row[ci] ?? '')}
                  >
                    {row[ci] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IcsEventCard({ url, onDownload }: { url: string; onDownload: () => void }) {
  const [event, setEvent] = useState<IcsEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- loading cycle when `url` changes
    setLoading(true);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setError(null);
    fetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then(text => setEvent(parseIcs(text)))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [url]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="text-center text-white p-8">
        <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-400" />
        <p className="text-red-400 mb-4">Could not load calendar event</p>
        <button onClick={onDownload} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <Download className="w-4 h-4 inline mr-2" />Download .ics
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-lg w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
      {/* Header stripe */}
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-5">
        <div className="flex items-center gap-3 mb-1">
          <Calendar className="w-6 h-6 text-white/80 flex-shrink-0" />
          <span className="text-sm font-medium text-white/80 uppercase tracking-wide">Calendar Invite</span>
        </div>
        <h2 className="text-xl font-bold text-white leading-tight">{event.summary}</h2>
        {event.status && event.status !== 'CONFIRMED' && (
          <span className="inline-block mt-1 text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">{event.status}</span>
        )}
      </div>

      {/* Details */}
      <div className="px-6 py-5 space-y-4">
        {/* Date/time */}
        {event.dtStart && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Calendar className="w-4 h-4 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{event.dtStart}</p>
              {event.dtEnd && event.dtEnd !== event.dtStart && (
                <p className="text-sm text-gray-500 dark:text-gray-400">until {event.dtEnd}</p>
              )}
            </div>
          </div>
        )}

        {/* Location */}
        {event.location && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
              <ExternalLink className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 pt-1">{event.location}</p>
          </div>
        )}

        {/* Organizer */}
        {event.organizer && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
              <FileText className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Organizer</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{event.organizer}</p>
            </div>
          </div>
        )}

        {/* Attendees */}
        {event.attendees.length > 0 && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
              <FileText className="w-4 h-4 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Attendees</p>
              <div className="space-y-0.5">
                {event.attendees.map((a, i) => (
                  <p key={i} className="text-sm text-gray-700 dark:text-gray-300">{a}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Description */}
        {event.description && (
          <div className="pt-1 border-t border-gray-100 dark:border-gray-700">
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Notes</p>
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{event.description}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 pb-5">
        <button
          onClick={onDownload}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium transition-colors"
        >
          <Download className="w-4 h-4" />
          Add to Calendar (.ics)
        </button>
      </div>
    </div>
  );
}

// Build proxy URL for a PDF so it can be rendered in an iframe (bypasses CORS/X-Frame-Options)
function getPdfProxyUrl(attachment: EmailAttachment, messageId?: string): string | null {
  if (!attachment.contentType.includes('pdf')) return null;
  const url = getAttachmentUrl(attachment, messageId);
  if (!url) return null;
  const filename = attachment.filename || 'document.pdf';

  // E2E-encrypted attachments: the URL already points to our own decrypt
  // endpoint (routed through the email API proxy).  On Capacitor, don't
  // wrap it in proxy-file — the URL is absolute and proxy-file's domain
  // allowlist would reject it, and fetchWithAuth adds the JWT header for
  // PdfCanvasViewer.  On web, still wrap in proxy-file so the iframe load
  // gets auth via cookie forwarding.
  const decryptMatch = /\/(emails\/)?attachments\/decrypt[?]/;
  const isDecrypt = decryptMatch.test(attachment.directUrl || '') || decryptMatch.test(attachment.url || '');
  if (isDecrypt && typeof window !== 'undefined' && isCapacitor()) {
    return url;
  }

  // Proxy all URLs (both cloud-storage and relative API URLs) through proxy-file
  // proxy-file handles relative URLs by resolving against our server and forwarding auth
  // On Capacitor, resolve to absolute URL so WebView <iframe> can load it
  return resolveUrlForPlatform(`/api/v1/proxy-file?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`);
}

// Attachment Preview Modal
function AttachmentPreviewModal({ attachment, messageId, isOpen, onClose, onEditPdf }: AttachmentPreviewModalProps) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // On Capacitor, use authenticated blob URLs for resources that need auth
  // CapacitorHttp intercepts fetch() but NOT <img>/<iframe> src attribute loads
  const [authedBlobUrl, setAuthedBlobUrl] = useState<string | null>(null);
  const isMobileApp = typeof window !== 'undefined' && isCapacitor();

  // Derive values - these need to be stable even when attachment is null
  const url = attachment ? getAttachmentUrl(attachment, messageId) : null;
  const isImage = attachment?.contentType?.startsWith('image/') ?? false;
  const isPdf = attachment?.contentType?.includes('pdf') ?? false;
  const isVideo = attachment?.contentType?.startsWith('video/') ?? false;
  const isAudio = attachment?.contentType?.startsWith('audio/') ?? false;
  const isIcs = attachment ? isIcsFile(attachment) : false;
  const isCsv = attachment ? isCsvFile(attachment) : false;
  const isOfficePreview = attachment ? isOfficePreviewAttachment(attachment) : false;

  const handleCsvLoaded = useCallback(() => setLoading(false), []);
  const handleCsvError = useCallback((msg: string) => {
    setLoading(false);
    setError(msg);
  }, []);

  useEffect(() => {
    if (isOpen && attachment && (isCsvFile(attachment) || isOfficePreviewAttachment(attachment))) {
      setLoading(true);
      setError(null);
    }
  }, [isOpen, attachment]);

  // Build proxy URL for PDFs — proxies through Next.js API to bypass CORS/X-Frame-Options
  // Include filename so the browser PDF viewer shows the correct title
  // On Capacitor, resolve to absolute URL
  const pdfProxyUrl = isPdf && url
    ? resolveUrlForPlatform(`/api/v1/proxy-file?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(attachment?.filename || 'document.pdf')}`)
    : null;

  // On Capacitor, fetch resources with auth headers and create blob URLs
  // This is needed because <img>/<iframe> src loads don't go through CapacitorHttp
  // and therefore don't include the JWT Authorization header
  useEffect(() => {
    // On Capacitor for PDFs, we show a static card instead of an iframe/blob.
    // Clear loading state immediately so the spinner doesn't stay visible.
    if (isOpen && isPdf && isMobileApp) {
      setLoading(false);
      return;
    }

    if (isCsv || isOfficePreview) {
      setAuthedBlobUrl(null);
      return;
    }

    if (!isOpen || !url || !isMobileApp) {
      setAuthedBlobUrl(null);
      return;
    }

    // Only need blob URL for resources that require auth (relative API URLs)
    // Cloud storage URLs are public and work directly
    // On Capacitor, any URL that resolves to our base domain needs auth headers
    // because WebView resource loads don't go through CapacitorHttp
    const config = typeof window !== 'undefined' ? (window as any).__CAPACITOR_BASE_URL__ : null;
    const debugInfo = {
      url,
      config,
      isPdf,
      isMobileApp,
      urlStartsWithConfig: config ? url.startsWith(config) : false,
      includesEmailApi: url.includes('/api/v1/email/messages/'),
      includesProxyFile: url.includes('/api/v1/proxy-file')
    };
    logToFile(`[AttachmentViewer] URL resolution debug: ${JSON.stringify(debugInfo)}`);
    const needsAuth = url.includes('/api/v1/email/messages/') ||
      url.includes('/api/v1/proxy-file') ||
      (config && url.startsWith(config));
    logToFile(`[AttachmentViewer] needsAuth decision: ${needsAuth}`);
    if (!needsAuth && !isPdf) {
      setAuthedBlobUrl(null);
      return;
    }

    let cancelled = false;
    const fetchUrl = isPdf && pdfProxyUrl ? pdfProxyUrl : url;

    const loadWithAuth = async () => {
      try {
        logToFile(`[AttachmentViewer] Fetching with auth: ${fetchUrl}`);
        const tokens = authService.getTokens();
        const headers: Record<string, string> = {};
        if (tokens?.access_token) {
          headers['Authorization'] = `Bearer ${tokens.access_token}`;
        }

        const response = await fetch(fetchUrl, { headers });
        logToFile(`[AttachmentViewer] Fetch response status: ${response.status}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const blob = await response.blob();
        logToFile(`[AttachmentViewer] Blob created, size: ${blob.size}, type: ${blob.type}`);
        if (!cancelled) {
          const blobUrl = URL.createObjectURL(blob);
          logToFile(`[AttachmentViewer] Blob URL created: ${blobUrl}`);
          setAuthedBlobUrl(blobUrl);
        }
      } catch (e) {
        logToFile(`[AttachmentViewer] Auth blob fetch failed: ${e instanceof Error ? e.message : String(e)}`);
        if (!cancelled) setAuthedBlobUrl(null);
      }
    };

    loadWithAuth();

    return () => {
      cancelled = true;
      if (authedBlobUrl) {
        URL.revokeObjectURL(authedBlobUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, url, isMobileApp, isPdf, pdfProxyUrl, isCsv]);

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Hooks must always be called - move useCallback before early return
  const handleDownload = useCallback(async () => {
    if (!url || !attachment) return;

    // On Capacitor, use native Filesystem + Share plugin
    if (isMobileApp) {
      try {
        const data = await fetchWithAuth(url);
        const blob = new Blob([data]);

        // Convert to base64 for Capacitor Filesystem
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1] || result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        const { Filesystem, Directory } = await import('@capacitor/filesystem');
        const filename = attachment.filename || 'attachment';
        const writeResult = await Filesystem.writeFile({
          path: filename,
          data: base64,
          directory: Directory.Cache,
        });

        // Use Capacitor Share plugin (handles file URIs natively)
        try {
          const { Share } = await import('@capacitor/share');
          await Share.share({
            title: filename,
            url: writeResult.uri,
          });
        } catch (shareErr: unknown) {
          // Share cancelled by user is not an error
          if (shareErr instanceof Error && shareErr.message.includes('cancel')) return;
          // Fallback: try navigator.share with File objects
          try {
            const file = new globalThis.File([blob], filename, { type: attachment.contentType || 'application/octet-stream' });
            await navigator.share({ files: [file] });
          } catch {
            notify.success('File saved to cache. Check your file manager to open it.');
          }
        }
      } catch (err) {
        console.error('[AttachmentViewer] Capacitor download failed:', err);
        notify.error('Download failed. Please try again.');
      }
      return;
    }

    const a = document.createElement('a');
    a.href = url;
    a.download = attachment.filename || 'attachment';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [url, attachment, isMobileApp]);

  const handleOpenInNewTab = useCallback(() => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [url]);

  // Guard against null attachment - after all hooks
  if (!isOpen || !attachment) return null;

  // Render via portal so modal escapes any parent stacking contexts
  // (fixes sidebar collapse / header minimize buttons showing on top)
  return createPortal(
    <div
      ref={modalRef}
      className="fixed inset-0 bg-black flex flex-col"
      style={{ zIndex: 999999, isolation: 'isolate', paddingTop: isMobileApp ? 'env(safe-area-inset-top)' : undefined, paddingBottom: isMobileApp ? 'env(safe-area-inset-bottom)' : undefined }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-gray-700">
        <div className="flex items-center gap-3 text-white min-w-0">
          <span className="font-medium truncate max-w-md">{attachment.filename}</span>
          <span className="text-sm text-gray-400 flex-shrink-0">{formatFileSize(attachment.size)}</span>
          <UserDocsLink docsPath="/total-connect/attachments" label="User guide: Attachments" className="flex-shrink-0 text-white/80 hover:text-white" />
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {isImage && (
            <>
              <button
                onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg"
                title="Zoom out"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              <span className="text-sm text-white/60 min-w-[50px] text-center">{Math.round(zoom * 100)}%</span>
              <button
                onClick={() => setZoom(z => Math.min(3, z + 0.25))}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg"
                title="Zoom in"
              >
                <ZoomIn className="w-5 h-5" />
              </button>
              <button
                onClick={() => setRotation(r => (r + 90) % 360)}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg"
                title="Rotate"
              >
                <RotateCw className="w-5 h-5" />
              </button>
            </>
          )}

          {isPdf && (
            <>
              {onEditPdf && pdfProxyUrl && (
                <button
                  onClick={() => onEditPdf(pdfProxyUrl, attachment?.filename || 'document.pdf')}
                  className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                  title="Edit, sign, and reply with this PDF"
                >
                  <PenTool className="w-4 h-4" />
                  <span className="hidden sm:inline">Edit &amp; Sign</span>
                </button>
              )}
              <button
                onClick={handleOpenInNewTab}
                className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                title="Open in new tab"
              >
                <ExternalLink className="w-4 h-4" />
                <span className="hidden sm:inline">Open in tab</span>
              </button>
            </>
          )}

          {isCsv && (
            <button
              onClick={handleOpenInNewTab}
              className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
              title="Open CSV in new tab"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="hidden sm:inline">Open in tab</span>
            </button>
          )}

          {isOfficePreview && (
            <button
              onClick={handleOpenInNewTab}
              className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
              title="Open attachment in new tab"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="hidden sm:inline">Open in tab</span>
            </button>
          )}

          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            title="Download"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Download</span>
          </button>

          {/* Close button - prominent */}
          <button
            onClick={onClose}
            className="p-2 bg-red-600/80 hover:bg-red-600 text-white rounded-lg ml-1"
            title="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content — click backdrop to close */}
      <div
        className="flex-1 flex items-center justify-center overflow-auto p-4 relative"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
        )}

        {error && (
          <div className="text-center text-white">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="mb-2 text-red-400">{error}</p>
            <div className="flex items-center gap-3 justify-center mt-4">
              <button
                onClick={handleOpenInNewTab}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
              >
                <ExternalLink className="w-4 h-4" />
                Open in new tab
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
          </div>
        )}

        {url && !error && (
          <>
            {isImage && (
              <img
                src={authedBlobUrl || url}
                alt={attachment.filename}
                className="max-w-full max-h-full object-contain transition-transform duration-200"
                style={{
                  transform: `scale(${zoom}) rotate(${rotation}deg)`,
                }}
                onLoad={() => setLoading(false)}
                onError={() => {
                  setLoading(false);
                  setError('Failed to load image');
                }}
              />
            )}

            {isPdf && pdfProxyUrl && !isMobileApp && (
              <iframe
                src={authedBlobUrl || pdfProxyUrl}
                className="w-full h-full bg-white dark:bg-gray-950 rounded-lg"
                title={attachment.filename}
                onLoad={() => setLoading(false)}
                onError={() => {
                  setLoading(false);
                  setError('Failed to load PDF');
                }}
              />
            )}

            {isPdf && url && isMobileApp && (
              /* On Capacitor/Android, render PDF pages via pdfjs-dist canvas.
                 Use the proxy URL so the fetch goes through our own server
                 origin — more reliable than cross-origin cloud-storage fetches
                 in the Capacitor WebView. */
              <PdfCanvasViewer
                url={pdfProxyUrl || url}
                onLoad={() => setLoading(false)}
                onError={() => {
                  setLoading(false);
                  setError('Failed to render PDF');
                }}
              />
            )}

            {isVideo && (
              <video
                src={authedBlobUrl || url}
                controls
                className="max-w-full max-h-full"
                onLoadedData={() => setLoading(false)}
                onError={() => {
                  setLoading(false);
                  setError('Failed to load video');
                }}
              />
            )}

            {isAudio && (
              <div className="bg-gray-800 p-8 rounded-xl">
                <audio
                  src={authedBlobUrl || url}
                  controls
                  className="w-80"
                  onLoadedData={() => setLoading(false)}
                  onError={() => {
                    setLoading(false);
                    setError('Failed to load audio');
                  }}
                />
              </div>
            )}

            {isCsv && url && (
              <CsvPreviewPanel
                url={url}
                onLoaded={handleCsvLoaded}
                onError={handleCsvError}
              />
            )}

            {isOfficePreview && url && (
              <OfficeAttachmentPreview
                url={url}
                filename={attachment.filename}
                onLoaded={() => setLoading(false)}
                onError={(message) => {
                  setLoading(false);
                  setError(message);
                }}
              />
            )}

            {!isImage && !isPdf && !isVideo && !isAudio && !isIcs && !isCsv && !isOfficePreview && (
              <div className="text-center text-white">
                <FileIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="mb-4">Preview not available for this file type</p>
                <div className="flex items-center gap-3 justify-center">
                  <button
                    onClick={handleOpenInNewTab}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open in new tab
                  </button>
                  <button
                    onClick={handleDownload}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Download file
                  </button>
                </div>
              </div>
            )}

            {isIcs && url && (
              <IcsEventCard url={url} onDownload={handleDownload} />
            )}
          </>
        )}

        {!url && (
          <div className="text-center text-white">
            <FileIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="mb-4">This attachment is not available for preview</p>
            <p className="text-sm text-gray-400">The file may have been deleted or is no longer accessible</p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * Authenticated Image component for Capacitor.
 * On Capacitor, <img> src loads bypass CapacitorHttp and don't include JWT auth.
 * For URLs that need auth (relative API paths), we fetch with auth headers
 * and render via blob URL. Cloud storage URLs work without auth.
 */
function AuthenticatedImg({
  src, alt, className, style, onLoad, onError, loading: lazyLoading
}: {
  src: string; alt: string; className?: string; style?: React.CSSProperties;
  onLoad?: () => void; onError?: () => void; loading?: 'lazy' | 'eager';
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const needsAuth = typeof window !== 'undefined' && isCapacitor() && src.includes('/api/v1/');

  useEffect(() => {
    if (!needsAuth) return;

    let cancelled = false;
    const load = async () => {
      try {
        const tokens = authService.getTokens();
        const headers: Record<string, string> = {};
        if (tokens?.access_token) headers['Authorization'] = `Bearer ${tokens.access_token}`;

        const res = await fetch(src, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (!cancelled) setBlobUrl(URL.createObjectURL(blob));
      } catch {
        if (!cancelled) onError?.();
      }
    };
    load();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, needsAuth]);

  return (
    <img
      src={needsAuth ? (blobUrl || '') : src}
      alt={alt}
      className={className}
      style={style}
      onLoad={onLoad}
      onError={onError}
      loading={lazyLoading}
    />
  );
}

/**
 * Helper: initialise pdfjs-dist lazily and return the library.
 * Sets the worker source to a local copy in /public (reliable on Capacitor
 * where CDN access from the WebView can be flaky).
 */
async function getPdfjsLib() {
  const pdfjsLib = await import('pdfjs-dist');
  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    // Use local worker from public/ (copied at build time from node_modules)
    // On Capacitor the base URL is different, so resolve relative to origin
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    pdfjsLib.GlobalWorkerOptions.workerSrc = `${base}/pdf.worker.min.mjs`;
  }
  return pdfjsLib;
}

/** Fetch a URL, adding auth headers for API routes. */
async function fetchWithAuth(url: string): Promise<ArrayBuffer> {
  logToFile(`[PdfCanvasViewer] fetchWithAuth called with URL: ${url}`);
  const headers: Record<string, string> = {};
  if (url.includes('/api/v1/') || url.includes('/api/mail/')) {
    const tokens = authService.getTokens();
    if (tokens?.access_token) headers['Authorization'] = `Bearer ${tokens.access_token}`;
    logToFile(`[PdfCanvasViewer] Added auth header for API URL`);
  }
  const res = await fetch(url, { headers });
  logToFile(`[PdfCanvasViewer] Fetch response status: ${res.status}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const buffer = await res.arrayBuffer();
  logToFile(`[PdfCanvasViewer] ArrayBuffer received, size: ${buffer.byteLength}`);
  return buffer;
}

/** Helper: check if a pdfjs error is a password/encryption error */
function isPdfPasswordError(err: unknown): boolean {
  if (!err) return false;
  const message = err instanceof Error ? `${err.name} ${err.message}` : String(err);
  const msg = message.toLowerCase();
  return msg.includes('password') || msg.includes('encrypted') || (err instanceof Error && err.name === 'PasswordException');
}

/**
 * PDF Canvas Thumbnail — renders the first page of a PDF to a <canvas> element
 * using pdfjs-dist. Works on Capacitor where iframe-based PDF rendering doesn't.
 * Shows a lock icon overlay for password-protected PDFs.
 */
function PdfCanvasThumbnail({
  url, className, onLoad, onError
}: {
  url: string; className?: string;
  onLoad?: () => void; onError?: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isEncrypted, setIsEncrypted] = useState(false);

  useEffect(() => {
    if (!url) { onError?.(); return; }

    let cancelled = false;

    const render = async () => {
      try {
        const pdfjsLib = await getPdfjsLib();
        const data = await fetchWithAuth(url);
        if (cancelled) return;

        const pdf = await pdfjsLib.getDocument({ data }).promise;
        const page = await pdf.getPage(1);

        const container = containerRef.current;
        const canvas = canvasRef.current;
        if (!container || !canvas || cancelled) return;

        const containerWidth = container.clientWidth || 160;
        const containerHeight = container.clientHeight || 120;

        const viewport = page.getViewport({ scale: 1 });
        const scale = Math.min(containerWidth / viewport.width, containerHeight / viewport.height);
        const scaledViewport = page.getViewport({ scale });

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        const ctx = canvas.getContext('2d');
        if (!ctx || cancelled) return;

        await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
        if (!cancelled) onLoad?.();
      } catch (err) {
        console.warn('[PdfCanvasThumbnail] Render failed:', err);
        if (!cancelled) {
          if (isPdfPasswordError(err)) {
            setIsEncrypted(true);
            onLoad?.(); // not a failure — show lock icon
          } else {
            onError?.();
          }
        }
      }
    };

    render();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  if (isEncrypted) {
    return (
      <div className={cn("w-full h-full flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-800 gap-1", className)}>
        <Lock className="w-8 h-8 text-amber-500" />
        <span className="text-[10px] text-gray-500 dark:text-gray-400">Protected</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn("w-full h-full flex items-center justify-center bg-white dark:bg-gray-950", className)}>
      <canvas ref={canvasRef} className="max-w-full max-h-full object-contain" />
    </div>
  );
}

/**
 * Full-page PDF viewer rendered via pdfjs-dist canvases.
 * Renders all pages in a scrollable column so the user can read the PDF
 * directly inside the preview modal on Capacitor.
 * Supports password-protected PDFs with an inline password prompt.
 */
function PdfCanvasViewer({
  url, onLoad, onError
}: {
  url: string;
  onLoad?: () => void; onError?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageCanvases, setPageCanvases] = useState<string[]>([]);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [loading, setLoading] = useState(true);
  const pdfDataRef = useRef<ArrayBuffer | null>(null);

  const renderPdf = useCallback(async (pdfPassword?: string) => {
    try {
      logToFile(`[PdfCanvasViewer] renderPdf called, url: ${url}`);
      setLoading(true);
      setRenderError(null);
      setPasswordError(false);

      const pdfjsLib = await getPdfjsLib();
      logToFile(`[PdfCanvasViewer] pdfjsLib loaded`);

      // Fetch PDF data only once, cache in ref
      if (!pdfDataRef.current) {
        logToFile(`[PdfCanvasViewer] Fetching PDF data...`);
        pdfDataRef.current = await fetchWithAuth(url);
        logToFile(`[PdfCanvasViewer] PDF data cached, size: ${pdfDataRef.current.byteLength}`);
      }

      const docParams: { data: ArrayBuffer; password?: string } = { data: pdfDataRef.current.slice(0) }; // slice to avoid detached buffer
      if (pdfPassword) docParams.password = pdfPassword;
      logToFile(`[PdfCanvasViewer] Loading PDF document...`);

      const pdf = await pdfjsLib.getDocument(docParams).promise;
      logToFile(`[PdfCanvasViewer] PDF loaded, numPages: ${pdf.numPages}`);
      const numPages = pdf.numPages;
      const images: string[] = [];

      const devicePixelRatio = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
      const targetWidth = (containerRef.current?.clientWidth || 360) * devicePixelRatio;
      logToFile(`[PdfCanvasViewer] Rendering ${numPages} pages at width: ${targetWidth}`);

      for (let i = 1; i <= numPages; i++) {
        logToFile(`[PdfCanvasViewer] Rendering page ${i}/${numPages}`);
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1 });
        const scale = targetWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        const offscreen = document.createElement('canvas');
        offscreen.width = scaledViewport.width;
        offscreen.height = scaledViewport.height;
        const ctx = offscreen.getContext('2d');
        if (!ctx) continue;

        await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
        images.push(offscreen.toDataURL('image/jpeg', 0.92));
      }

      setPageCanvases(images);
      setNeedsPassword(false);
      setLoading(false);
      onLoad?.();
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      logToFile(`[PdfCanvasViewer] Render failed: ${errorMsg}`);
      console.warn('[PdfCanvasViewer] Render failed:', err);
      if (isPdfPasswordError(err)) {
        if (pdfPassword) {
          // Wrong password
          setPasswordError(true);
          setLoading(false);
        } else {
          // First time — show password prompt
          setNeedsPassword(true);
          setLoading(false);
          onLoad?.(); // not a fatal error
        }
      } else {
        setRenderError(err instanceof Error ? err.message : 'Failed to render PDF');
        setLoading(false);
        onError?.();
      }
    }
  }, [url, onLoad, onError]);

  useEffect(() => {
    if (!url) { onError?.(); return; }
    renderPdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.trim()) renderPdf(password.trim());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-sm shadow-xl">
          <div className="flex flex-col items-center mb-4">
            <Lock className="w-10 h-10 text-amber-500 mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Password Required</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-1">
              This PDF is password-protected.
            </p>
          </div>
          <form onSubmit={handlePasswordSubmit} className="space-y-3">
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setPasswordError(false); }}
              placeholder="Enter password"
              autoFocus
              className={cn(
                "w-full px-4 py-3 rounded-xl border text-base bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 outline-none focus:ring-2 focus:ring-blue-500",
                passwordError ? "border-red-500" : "border-gray-300 dark:border-gray-600"
              )}
            />
            {passwordError && (
              <p className="text-sm text-red-500">Incorrect password. Please try again.</p>
            )}
            <button
              type="submit"
              disabled={!password.trim()}
              className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-base"
            >
              Unlock PDF
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (renderError) {
    return (
      <div className="text-center text-white p-8">
        <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
        <p className="text-red-400 mb-2">{renderError}</p>
      </div>
    );
  }

  if (pageCanvases.length === 0) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full h-full overflow-auto flex flex-col items-center gap-2 py-2">
      {pageCanvases.map((dataUrl, i) => (
        <img
          key={i}
          src={dataUrl}
          alt={`Page ${i + 1}`}
          className="w-full max-w-[95%] bg-white dark:bg-gray-950 rounded shadow-lg"
        />
      ))}
    </div>
  );
}

// Main component - displays attachment thumbnails/cards
export default function EmailAttachmentViewer({ attachments, messageId, className, htmlBody, onReplyWithAttachment, onSavedToCloud, onRemoveAttachment, forceCompact, defaultCollapsed }: EmailAttachmentViewerProps) {
  const [selectedAttachment, setSelectedAttachment] = useState<EmailAttachment | null>(null);
  const [loadingThumbs, setLoadingThumbs] = useState<Record<number, boolean>>({});
  const [errorThumbs, setErrorThumbs] = useState<Record<number, boolean>>({});
  const [editingPdf, setEditingPdf] = useState<{ url: string; filename: string } | null>(null);
  const [thumbnailsCollapsed, setThumbnailsCollapsed] = useState(defaultCollapsed ?? false);
  const isMobileApp = typeof window !== 'undefined' && isCapacitor();

  // Open PDF editor from preview modal or thumbnail
  const handleEditPdf = useCallback((pdfUrl: string, filename: string) => {
    setSelectedAttachment(null); // Close preview modal
    setEditingPdf({ url: pdfUrl, filename });
  }, []);

  // Show all attachments, including embedded/inline images.
  // Previously we filtered out embedded images to avoid duplication with
  // CID-referenced images in the HTML body, but the user wants every
  // attachment visible in the attachment list.
  const displayAttachments = attachments || [];

  if (!displayAttachments || displayAttachments.length === 0) return null;

  // Dynamic sizing based on number of attachments (forceCompact overrides to compact sizing)
  const attachmentCount = displayAttachments.length;
  const isCompact = forceCompact || attachmentCount > 4;
  const isVeryCompact = attachmentCount > 8;

  // Thumbnail height based on count
  const thumbHeight = isVeryCompact ? 'h-[60px]' : isCompact ? 'h-[80px]' : 'h-[120px]';
  const maxWidth = isVeryCompact ? 'max-w-[100px]' : isCompact ? 'max-w-[120px]' : 'max-w-[160px]';
  const gridCols = isVeryCompact
    ? 'grid-cols-4 sm:grid-cols-6 lg:grid-cols-8'
    : isCompact
      ? 'grid-cols-3 sm:grid-cols-4 lg:grid-cols-6'
      : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';
  const iconSize = isVeryCompact ? 'w-6 h-6' : isCompact ? 'w-8 h-8' : 'w-10 h-10';
  const fontSize = isVeryCompact ? 'text-[9px]' : 'text-xs';
  const sizeFont = isVeryCompact ? 'text-[8px]' : 'text-[10px]';

  return (
    <>
      <div className={cn("w-full", className)}>
        {/* Accordion toggle header */}
        <button
          type="button"
          onClick={() => setThumbnailsCollapsed(prev => !prev)}
          className="flex items-center gap-1.5 w-full px-2 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        >
          <Paperclip className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="flex-1 text-left">
            {displayAttachments.length} Attachment{displayAttachments.length > 1 ? 's' : ''}
          </span>
          <ChevronDown className={cn("w-4 h-4 flex-shrink-0 transition-transform", thumbnailsCollapsed && "-rotate-90")} />
        </button>

        {/* Grid layout: dynamic cols based on attachment count */}
        {!thumbnailsCollapsed && (
        <div className={cn("grid gap-2 mt-1", gridCols)}>
          {displayAttachments.map((attachment, index) => {
            const FileIcon = getFileIcon(attachment.contentType, attachment.filename);
            const isImage = attachment.contentType.startsWith('image/');
            const isPdf = attachment.contentType.includes('pdf');
            const thumbUrl = getThumbnailUrl(attachment, messageId);
            const pdfThumbUrl = getPdfProxyUrl(attachment, messageId);
            const url = getAttachmentUrl(attachment, messageId);
            const canView = canPreview(attachment.contentType, attachment.filename) && Boolean(url);

            return (
              <div
                key={index}
                className={cn(
                  "group relative bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden",
                  "w-full", maxWidth,
                  canView ? "cursor-pointer hover:border-blue-500 dark:hover:border-blue-400" : ""
                )}
                onClick={() => canView && setSelectedAttachment(attachment)}
              >
                {/* Thumbnail or Icon - dynamic height */}
                <div className={cn(thumbHeight, "w-full flex items-center justify-center bg-gray-100 dark:bg-gray-700/50 overflow-hidden relative")}>
                  {isImage && thumbUrl && !errorThumbs[index] ? (
                    <>
                      {loadingThumbs[index] && (
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin absolute" />
                      )}
                      <AuthenticatedImg
                        src={thumbUrl}
                        alt={attachment.filename}
                        className={cn(
                          "w-full h-full object-cover",
                          loadingThumbs[index] ? "opacity-0" : "opacity-100"
                        )}
                        onLoad={() => setLoadingThumbs(prev => ({ ...prev, [index]: false }))}
                        onError={() => {
                          setLoadingThumbs(prev => ({ ...prev, [index]: false }));
                          setErrorThumbs(prev => ({ ...prev, [index]: true }));
                        }}
                        loading="lazy"
                      />
                    </>
                  ) : isPdf && pdfThumbUrl && !errorThumbs[index] && !isMobileApp ? (
                    <>
                      {/* PDF thumbnail: scaled-down iframe rendering first page (desktop only — iframe auth doesn't work on Capacitor) */}
                      {loadingThumbs[index] !== false && (
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin absolute z-10" />
                      )}
                      <div className="w-full h-full overflow-hidden bg-white dark:bg-gray-950">
                        <iframe
                          src={`${pdfThumbUrl}#toolbar=0&navpanes=0&scrollbar=0&page=1&view=FitH`}
                          className="border-0 pointer-events-none"
                          style={{
                            width: '595px',
                            height: '842px',
                            transform: `scale(${isVeryCompact ? 0.08 : isCompact ? 0.11 : 0.17})`,
                            transformOrigin: 'top left',
                          }}
                          scrolling="no"
                          tabIndex={-1}
                          loading="lazy"
                          title={attachment.filename}
                          onLoad={() => setLoadingThumbs(prev => ({ ...prev, [index]: false }))}
                          onError={() => {
                            setLoadingThumbs(prev => ({ ...prev, [index]: false }));
                            setErrorThumbs(prev => ({ ...prev, [index]: true }));
                          }}
                        />
                      </div>
                    </>
                  ) : isPdf && url && isMobileApp && !errorThumbs[index] ? (
                    <>
                      {/* Capacitor PDF thumbnail: canvas-rendered first page via pdfjs-dist */}
                      {loadingThumbs[index] !== false && (
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin absolute z-10" />
                      )}
                      <PdfCanvasThumbnail
                        url={pdfThumbUrl || url}
                        onLoad={() => setLoadingThumbs(prev => ({ ...prev, [index]: false }))}
                        onError={() => {
                          setLoadingThumbs(prev => ({ ...prev, [index]: false }));
                          setErrorThumbs(prev => ({ ...prev, [index]: true }));
                        }}
                      />
                    </>
                  ) : (
                    <FileIcon className={cn(iconSize, "text-gray-400")} />
                  )}

                  {/* Hover overlay */}
                  {canView && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <Maximize2 className={cn(isCompact ? "w-4 h-4" : "w-6 h-6", "text-white opacity-0 group-hover:opacity-100 transition-opacity")} />
                    </div>
                  )}
                </div>

                {/* File info */}
                <div className={cn(isVeryCompact ? "p-1" : "p-2")}>
                  <p className={cn(fontSize, "font-medium text-gray-700 dark:text-gray-300 truncate")} title={attachment.filename}>
                    {attachment.filename}
                  </p>
                  <p className={cn(sizeFont, "text-gray-500 dark:text-gray-400")}>
                    {formatFileSize(attachment.size)}
                  </p>
                </div>

                {/* Action buttons overlay */}
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {/* Remove button (composer mode) */}
                  {onRemoveAttachment && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveAttachment(index);
                      }}
                      className={cn(
                        "bg-red-500/90 rounded-full hover:bg-red-600",
                        isCompact ? "p-1" : "p-1.5"
                      )}
                      title="Remove attachment"
                    >
                      <X className={cn(isCompact ? "w-3 h-3" : "w-3.5 h-3.5", "text-white")} />
                    </button>
                  )}
                  {/* Edit & Sign button for PDFs */}
                  {isPdf && url && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const proxyUrl = getPdfProxyUrl(attachment, messageId);
                        if (proxyUrl) handleEditPdf(proxyUrl, attachment.filename);
                      }}
                      className={cn(
                        "bg-emerald-500/90 dark:bg-emerald-600/90 rounded-full hover:bg-emerald-600 dark:hover:bg-emerald-500",
                        isCompact ? "p-1" : "p-1.5"
                      )}
                      title="Edit & Sign PDF"
                    >
                      <PenTool className={cn(isCompact ? "w-3 h-3" : "w-3.5 h-3.5", "text-white")} />
                    </button>
                  )}

                  {/* Download button */}
                  {url && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        // On Capacitor, use native Filesystem + share sheet
                        if (typeof window !== 'undefined' && isCapacitor()) {
                          try {
                            const tokens = authService.getTokens();
                            const headers: Record<string, string> = {};
                            if (tokens?.access_token) headers['Authorization'] = `Bearer ${tokens.access_token}`;
                            const res = await fetch(url, { headers });
                            if (!res.ok) throw new Error(`HTTP ${res.status}`);
                            const blob = await res.blob();
                            const base64 = await new Promise<string>((resolve, reject) => {
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                const result = reader.result as string;
                                resolve(result.split(',')[1] || result);
                              };
                              reader.onerror = reject;
                              reader.readAsDataURL(blob);
                            });
                            const { Filesystem, Directory } = await import('@capacitor/filesystem');
                            const fname = attachment.filename || 'attachment';
                            const writeResult = await Filesystem.writeFile({
                              path: fname,
                              data: base64,
                              directory: Directory.Cache,
                            });
                            if (navigator.share) {
                              await navigator.share({ title: fname, url: writeResult.uri });
                            } else {
                              window.open(writeResult.uri, '_system');
                            }
                          } catch (err) {
                            console.error('[AttachmentViewer] Capacitor thumbnail download failed:', err);
                          }
                          return;
                        }
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = attachment.filename || 'attachment';
                        a.target = '_blank';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }}
                      className={cn(
                        "bg-white/90 dark:bg-gray-800/90 rounded-full hover:bg-white dark:hover:bg-gray-700",
                        isCompact ? "p-1" : "p-1.5"
                      )}
                      title="Download"
                    >
                      <Download className={cn(isCompact ? "w-3 h-3" : "w-3.5 h-3.5", "text-gray-600 dark:text-gray-400")} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* Preview Modal */}
      <AttachmentPreviewModal
        attachment={selectedAttachment!}
        messageId={messageId}
        isOpen={selectedAttachment !== null}
        onClose={() => setSelectedAttachment(null)}
        onEditPdf={handleEditPdf}
      />

      {/* PDF Editor Overlay */}
      {editingPdf && (
        <PDFEditorOverlay
          pdfUrl={editingPdf.url}
          filename={editingPdf.filename}
          isOpen={true}
          onClose={() => setEditingPdf(null)}
          onReplyWithAttachment={onReplyWithAttachment}
          onSavedToCloud={onSavedToCloud}
          messageId={messageId}
        />
      )}
    </>
  );
}
